/**
 * Admissions CRM — Activity Feed API contract (plan #001, U5).
 *
 * GET  /v1/enrollment/activity      — the "what changed" feed.
 * POST /v1/enrollment/activity/seen — advance the viewer's last-visit marker.
 *
 * The feed returns candidate status-change journal rows since the viewer's
 * server-stored last-visit marker, CLAMPED so the first wide backfill never
 * renders as overnight change: window = `max(lastVisitedAt, firstSuccessfulSync,
 * now − 48h)` (R10, KTD14). Rows for candidates outside the year-scoped mirror
 * are suppressed. Enrolled transitions are flagged as wins. The mark-seen write
 * is MONOTONIC — the marker only advances.
 *
 * Admin-only, school-scoped; the marker is the admin's own `UserPreference`
 * (namespace `admissions`, key `lastVisitedAt`).
 */

import { z } from 'zod'

/** One activity-feed row — a mirrored candidate status transition. */
export const EnrollmentActivityRowSchema = z.object({
  /** Journal row publicId. */
  publicId: z.string(),
  /** BB candidate id of the transition's subject. */
  candidateId: z.string(),
  /** Candidate first name, or null. */
  firstName: z.string().nullable(),
  /** Candidate last name, or null. */
  lastName: z.string().nullable(),
  /** The stage id the candidate moved INTO, or null. */
  statusId: z.number().int().nullable(),
  /** Snapshot stage label joined from the catalog (KTD11), or null. */
  statusDescription: z.string().nullable(),
  /** Transition timestamp (ISO 8601). */
  changedAt: z.string(),
  /** True when this transition is an Enrolled "win" (R10, decided from mirror). */
  isWin: z.boolean()
})
export type EnrollmentActivityRow = z.infer<typeof EnrollmentActivityRowSchema>

export const GetEnrollmentActivityResponseSchema = z.object({
  /** Start of the feed window (ISO 8601) — the clamped marker (R10). */
  since: z.string(),
  /**
   * True when this is the viewer's FIRST visit (no stored marker) so the window
   * fell back to the 48-hour default — the panel shows a "last 48 hours" label.
   */
  isFirstVisit: z.boolean(),
  /** True when the row list was capped server-side (bounded-work pattern). */
  isTruncated: z.boolean(),
  /** The feed rows, newest first, within the clamped window. */
  rows: z.array(EnrollmentActivityRowSchema)
})
export type GetEnrollmentActivityResponse = z.infer<typeof GetEnrollmentActivityResponseSchema>

/**
 * POST body for mark-seen: the timestamp the viewer is marking seen UP TO
 * (ISO 8601, defaults server-side to `now`). The write is monotonic — a value
 * older than the stored marker is ignored (never winds the marker back).
 */
export const MarkActivitySeenRequestSchema = z.object({
  /** Optional explicit seen-up-to instant (ISO 8601); server defaults to now. */
  seenAt: z.string().datetime().optional()
})
export type MarkActivitySeenRequest = z.infer<typeof MarkActivitySeenRequestSchema>

export const MarkActivitySeenResponseSchema = z.object({
  /** The marker value after the (monotonic) write (ISO 8601). */
  lastVisitedAt: z.string(),
  /** True when this call actually advanced the marker (false = no-op replay). */
  advanced: z.boolean()
})
export type MarkActivitySeenResponse = z.infer<typeof MarkActivitySeenResponseSchema>
